import { ChangeEvent, Dispatch, useContext, useEffect, useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { Modal } from 'antd';
import styled from 'styled-components';
import QuestionMark from '../../../../../assets/images/svg/question-green-icon.svg';
import { ProductListingContext } from '../../../functions/data_formatting_functions';
import {
  actionSetAllAttributes,
  actionSetListingData,
} from '../../../store/create_listing_actions';
import { toast } from 'react-toastify';
import { Colors } from '../../../../../config/theme';
import {
  AttributeNameField,
  TranslateButton,
} from '../../../components/create_listings_components';
import { AttributeType } from '../../../../../data/types/attributes';
import UtilitiesRepository from '../../../../../data/repositories/utilities_repository';
import { ListingState } from '../../../../../data/types/listings';
import {
  BaseNumericAttribute,
  BaseSelectAttribute,
} from '../../../../../data/types/attributes';
import { Icon } from '@iconify/react';
import { Button } from '@nextui-org/react';

type CustomAttributeModalProps = {
  isModalVisible: boolean;
  setIsModalVisible: (val: boolean) => void;
  handleOk: () => void;
};

const CustomAttributeModal = ({
  isModalVisible,
  setIsModalVisible,
  handleOk,
}: CustomAttributeModalProps) => {
  const options = [
    { label: 'Text', value: AttributeType.SELECT },
    { label: 'Boolean', value: AttributeType.BOOLEAN },
    { label: 'Numeric', value: AttributeType.NUMERIC },
  ];
  const EMPTY_OPTION = 'Select Option';

  const initialValues = {
    name_en: '',
    name_ar: '',
    type: EMPTY_OPTION,
  };
  const {
    state,
    dispatch,
  }: {
    state: ListingState;
    dispatch: Dispatch<any>;
  } = useContext(ProductListingContext);
  const [selectedOption, setSelectedOption] = useState<number | string>(
    'Select Attribute Type',
  );
  const [attribute, setAttribute] = useState<any>(initialValues);

  useEffect(() => {
    setAttribute(initialValues);
  }, [isModalVisible]);

  useEffect(() => {
    if (attribute.type === AttributeType.SELECT) {
      setAttribute({
        ...attribute,
        official_values_en: attribute.official_values_en,
        official_values_ar: attribute.official_values_ar,
      });
    } else {
      setAttribute({
        ...attribute,
        values: attribute.values,
      });
    }
  }, [
    attribute.official_values_en,
    attribute.official_values_ar,
    attribute.values,
  ]);

  function getOption(val: ChangeEvent<HTMLSelectElement>) {
    if (selectedOption !== Number(val.target.value)) {
      const attr = attribute;
      delete attr.values;
      delete attr.values_ar;
      delete attr.values_en;
      delete attr.official_values_ar;
      delete attr.official_values_en;
      setAttribute(attr);
    }
    setSelectedOption(Number(val.target.value));
    if (Number(val.target.value) === AttributeType.SELECT) {
      setAttribute({
        ...attribute,
        official_values_en: [''],
        official_values_ar: [''],
        values_en: [''],
        values_ar: [''],
        type: val.target.value,
      });
    } else if (Number(val.target.value) === AttributeType.NUMERIC) {
      setAttribute({
        ...attribute,
        unit: '',
        values: [''],
        type: val.target.value,
      });
    } else {
      setAttribute({
        ...attribute,
        values: [''],
        type: val.target.value,
      });
    }
  }

  const onChangeSelectValues = (
    value: string,
    index: number,
    { ar = false } = {},
  ) => {
    function updateSelectAttributeValues(attr: BaseSelectAttribute) {
      if (ar) {
        attr.values_ar[index] = value;
        attr.official_values_ar[index] = value;
      } else {
        attr.values_en[index] = value;
        attr.official_values_en[index] = value;
      }
      setAttribute({
        ...attr,
        official_values_en: [...attr.official_values_en],
        official_values_ar: [...attr.official_values_ar],
        values_en: [...attr.values_en],
        values_ar: [...attr.values_ar],
      });
    }
    updateSelectAttributeValues(attribute as BaseSelectAttribute);
  };

  const onChangeNumericValues = (value: string, index: number) => {
    function updateNumericAttributeValues(
      attr: BaseNumericAttribute,
      index: number,
    ) {
      attribute.values[index] = value;
      setAttribute({
        ...attr,
        values: [...attr.values.filter((i: string) => i != '')],
      });
      return;
    }
    updateNumericAttributeValues(attribute as BaseNumericAttribute, index);
  };

  const reset_fields = () => {
    setSelectedOption(EMPTY_OPTION);
    setAttribute({ ...attribute, type: Number(EMPTY_OPTION) });
  };

  const submit = () => {
    function validateSelectAttribute(attr: BaseSelectAttribute) {
      console.log(attr);
      // prevent adding empty attribute where values is empty string in both official_values_en and official_values_ar in the same index:
      for (let i = 0; i < attr.official_values_en.length; i++) {
        if (
          attr.official_values_en[i] === '' &&
          attr.official_values_ar[i] === ''
        ) {
          toast.warning('You cannot add an empty option!');
          return false;
        }
      }
      if (
        attr.official_values_en.filter((i: string) => i != '').length < 1 &&
        attr.official_values_ar.filter((i: string) => i != '').length < 1
      ) {
        toast.warning('Please add at least one value in any language!');
        return false;
      }
      // Used to to prevent adding specific language attribute name without filling all values in the same language
      // if (
      //   attr.name_en !== '' &&
      //   attr.name_ar === '' &&
      //   attr.official_values_en.includes('')
      // ) {
      //   toast.warning(
      //     'If you added English name, please fill at least all English options',
      //   );
      //   return false;
      // } else if (
      //   attr.name_ar !== '' &&
      //   attr.name_en === '' &&
      //   attr.official_values_ar.includes('')
      // ) {
      //   toast.warning(
      //     'If you added Arabic name, please fill at least all Arabic options',
      //   );
      //   return false;
      // }
      if (
        attr.official_values_ar.includes('') ||
        attr.official_values_en.includes('')
      ) {
        toast.warning(
          'Please fill all values in both languages before adding the attribute!',
        );
        return false;
      }
      return true;
    }

    function validateNumericAttribute(attr: BaseNumericAttribute) {
      for (let i = 0; i < attr.values.length; i++) {
        if (attr.values[i] === '' && attr.values[i] === '') {
          toast.warning('You cannot add an empty option!');
          return false;
        }
      }
      if (attr.values.filter((i: string) => i != '').length < 1) {
        toast.warning('Please select at least one value!');
        return false;
      }
      return true;
    }

    if (attribute.name_en === '' && attribute.name_ar === '') {
      toast.warning('Please fill name field in at least one language!');
      return;
    }

    if (!attribute.type || attribute.type === 'Select Attribute Type') {
      toast.warning('Please select attribute type!');
      return;
    }

    if (
      attribute.name_en !== '' &&
      attribute.name_ar !== '' &&
      state.all_attributes.find(
        (attr) =>
          attr.name_en === attribute.name_en ||
          attr.name_ar === attribute.name_ar,
      )
    ) {
      toast.warning('Attribute name already exists!');
      return;
    }

    if (
      (attribute.type === AttributeType.SELECT ||
        Number(attribute.type) === AttributeType.SELECT) &&
      !validateSelectAttribute(attribute as BaseSelectAttribute)
    ) {
      // Custom Select Attribute Verficaition
      return;
    }

    if (
      (attribute.type === AttributeType.NUMERIC ||
        Number(attribute.type) === AttributeType.NUMERIC) &&
      !validateNumericAttribute(attribute as BaseNumericAttribute)
    ) {
      // Custom Numeric Attribute Verficaition
      return;
    }

    const attr = {
      ...attribute,
      type: Number(attribute.type),
      custom: true,
      is_variable: attribute.values?.length > 1 || false,
    };
    if (AttributeType.SELECT === attr.type) {
      // Values_en & Values_ar represent currently selected Select Attribute values, to prevent incorrect values choosing, we need to reset them to empty array
      attr.values_en = [];
      attr.values_ar = [];
    }
    dispatch(
      actionSetListingData({
        ...state.data,
        selectedImages: { ...state.data.selectedImages, [attr.name_en]: [] },
      }),
    );

    dispatch(actionSetAllAttributes([...state.all_attributes, attr]));

    if (attr.type === AttributeType.NUMERIC) {
      dispatch(
        actionSetListingData({
          ...state.data,
          numeric_attrs: [...state.data.numeric_attrs, attr],
        }),
      );
    }
    if (attr.type === AttributeType.SELECT) {
      dispatch(
        actionSetListingData({
          ...state.data,
          select_attrs: [...state.data.select_attrs, attr],
        }),
      );
    }

    if (attr.type === AttributeType.BOOLEAN) {
      dispatch(
        actionSetListingData({
          ...state.data,
          boolean_attrs: [...state.data.boolean_attrs, attr],
        }),
      );
    }

    // appendAttribute(attribute);
    reset_fields();
    setIsModalVisible(false);
  };

  const renderSelectValues = () => {
    return (
      <div>
        <div className="option">
          <label>Options</label>
          {attribute?.official_values_en?.map((_: string, index: number) => {
            const MoveToNextInputOrAddNewValueSelect = (
              e: React.KeyboardEvent<HTMLInputElement>,
              index: number,
              pressedAr: boolean,
            ) => {
              if (state.productType == 1 && e.key === 'Enter') {
                e.preventDefault();
                if (index === attribute.values_en.length - 1) {
                  setAttribute({
                    ...attribute,
                    official_values_en: attribute.official_values_en
                      ? [...attribute.official_values_en, '']
                      : [''],
                    values_en: attribute.values_en
                      ? [...attribute.values_en, '']
                      : [''],
                    official_values_ar: attribute.official_values_ar
                      ? [...attribute.official_values_ar, '']
                      : [''],
                    values_ar: attribute.values_ar
                      ? [...attribute.values_ar, '']
                      : [''],
                  });
                } else {
                  // Shift focus to the next input field
                  const nextInput = document.getElementById(
                    `input-${pressedAr ? 'ar' : 'en'}-${index + 1}`,
                  ) as HTMLInputElement;
                  if (nextInput) nextInput.focus();
                }
              }
            };
            return (
              <div
                key={index}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '10px',
                }}
              >
                <input
                  key={index}
                  type={'text'}
                  id={`input-en-${index}`}
                  onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) =>
                    MoveToNextInputOrAddNewValueSelect(e, index, false)
                  }
                  value={attribute.values_en[index] || ''}
                  onChange={(e) => onChangeSelectValues(e.target.value, index)}
                  className="form-control"
                  placeholder="Red"
                />
                <TranslateButton
                  leftToRightCallback={async () => {
                    const word = attribute.values_en[index];
                    await UtilitiesRepository.translateText(word).then(
                      (res: string) => {
                        onChangeSelectValues(res, index, {
                          ar: true,
                        });
                      },
                    );
                  }}
                  rightToLeftCallback={async () => {
                    const word = attribute.values_ar[index];
                    await UtilitiesRepository.translateText(word, true).then(
                      (res: string) => {
                        onChangeSelectValues(res, index);
                      },
                    );
                  }}
                />
                <input
                  key={index + 100}
                  id={`input-ar-${index}`}
                  onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) =>
                    MoveToNextInputOrAddNewValueSelect(e, index, true)
                  }
                  type={'text'}
                  value={attribute.values_ar[index] || ''}
                  onChange={(e) =>
                    onChangeSelectValues(e.target.value, index, { ar: true })
                  }
                  className="form-control"
                  placeholder="أحمر"
                  style={{
                    direction: 'rtl',
                  }}
                />
                <Icon
                  icon="akar-icons:trash-can"
                  style={{
                    color: Colors.brightRed,
                    cursor: 'pointer',
                    marginLeft: '10px',
                    marginBottom: '10px',
                  }}
                  height={50}
                  width={50}
                  onClick={() => {
                    const newOfficialValuesEn =
                      attribute.official_values_en.filter(
                        (_: any, i: number) => i !== index,
                      );
                    const newOfficialValuesAr =
                      attribute.official_values_ar.filter(
                        (_: any, i: number) => i !== index,
                      );
                    const newValuesEn = attribute.values_en.filter(
                      (_: any, i: number) => i !== index,
                    );
                    const newValuesAr = attribute.values_ar.filter(
                      (_: any, i: number) => i !== index,
                    );
                    setAttribute({
                      ...attribute,
                      official_values_en: newOfficialValuesEn,
                      official_values_ar: newOfficialValuesAr,
                      values_en: newValuesEn,
                      values_ar: newValuesAr,
                    });
                  }}
                />
              </div>
            );
          })}
        </div>
        {state.productType === 1 && (
          <div
            className="text-center"
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Button
              onClick={() => {
                if (
                  attribute?.official_values_en.length === 1 ||
                  attribute?.values_en.length === 1
                ) {
                  return;
                }
                setAttribute({
                  ...attribute,
                  official_values_en: attribute.official_values_en.slice(0, -1),
                  official_values_ar: attribute.official_values_ar.slice(0, -1),
                  values_ar: attribute.values_ar.slice(0, -1),
                  values_en: attribute.values_en.slice(0, -1),
                });
              }}
              style={{
                backgroundColor: Colors.brightRed,
                color: Colors.white,
                flex: 1,
              }}
            >
              Remove option -
            </Button>
            <div style={{ width: '10px' }} />
            <Button
              className="butn2"
              style={{
                background: 'white',
                color: '#05AE4B',
              }}
              onClick={() =>
                setAttribute({
                  ...attribute,
                  official_values_en: attribute.official_values_en
                    ? [...attribute.official_values_en, '']
                    : [''],
                  values_en: attribute.values_en
                    ? [...attribute.values_en, '']
                    : [''],
                  official_values_ar: attribute.official_values_ar
                    ? [...attribute.official_values_ar, '']
                    : [''],
                  values_ar: attribute.values_ar
                    ? [...attribute.values_ar, '']
                    : [''],
                })
              }
            >
              Add option +
            </Button>
          </div>
        )}
      </div>
    );
  };

  const renderNumericValues = () => {
    return (
      <div>
        <div className="option">
          <label>Options</label>
          {attribute.values.map((value: string, index: number) => {
            const MoveToNextInputOrAddNewValueNumeric = (
              e: React.KeyboardEvent<HTMLInputElement>,
            ) => {
              if (state.productType == 1 && e.key === 'Enter') {
                // 13 is the keyCode for "Enter"
                e.preventDefault();
                if (index === attribute.values.length - 1) {
                  setAttribute({
                    ...attribute,
                    values: [...attribute.values, ''],
                  });
                } else {
                  // Shift focus to the next input field
                  const nextInput = document.getElementById(
                    `input-${index + 1}`,
                  ) as HTMLInputElement;
                  if (nextInput) nextInput.focus();
                }
              }
            };
            return (
              <input
                key={index}
                value={value}
                id={`input-${index}`}
                onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) =>
                  MoveToNextInputOrAddNewValueNumeric(e)
                }
                onChange={(e) => {
                  const enteredValue = e.target.value;
                  if (enteredValue !== '' && !/^[0-9]+$/.test(enteredValue)) {
                    toast.warn('Please enter only digits 0-9');
                    return;
                  }
                  onChangeNumericValues(enteredValue, index);
                }}
                className="form-control"
                placeholder="256"
              />
            );
          })}
        </div>
        {state.productType === 1 && (
          <div
            className="text-center"
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Button
              onClick={() => {
                if (
                  attribute?.values.length === 1 ||
                  attribute?.values.length === 1
                ) {
                  return;
                }
                setAttribute({
                  ...attribute,
                  values: attribute.values.slice(0, -1),
                });
              }}
              style={{
                backgroundColor: Colors.brightRed,
                color: Colors.white,
                flex: 1,
              }}
            >
              Remove option -
            </Button>
            <div style={{ width: '10px' }} />
            <Button
              className="butn2"
              style={{
                background: 'white',
                color: '#05AE4B',
              }}
              onClick={() =>
                setAttribute({
                  ...attribute,
                  values: [...attribute.values, ''],
                })
              }
            >
              Add option +
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal
      open={isModalVisible}
      className="right-slide-modal w-30"
      footer={false}
      onCancel={() => {
        reset_fields();
        setIsModalVisible(false);
      }}
      onOk={handleOk}
    >
      <Wrapper>
        <div className="head_title">
          <Offcanvas.Title>Custom field</Offcanvas.Title>
        </div>
        <div className="containerr">
          <div>
            <p>
              Choose one of the available types, defining field types will
              enhance the reach of this listing to your customers
            </p>
          </div>
          <div className="dropdown">
            <label>Attribute Type</label>
            <select
              value={selectedOption}
              onChange={(val) => getOption(val)}
              className="options"
            >
              <option>{EMPTY_OPTION}</option>
              {options.map((option, index) => {
                return (
                  <option key={index} value={option.value}>
                    {option.label}
                  </option>
                );
              })}
            </select>
          </div>
          {selectedOption === AttributeType.BOOLEAN && (
            <div className="Bool">
              <div className=" contents">
                <img src={QuestionMark} alt="..." />
                <p>
                  Boolean attributes can be either true or false or both about
                  your product like waterproof, Eco-friendly.
                </p>
              </div>{' '}
              <div className="screen">
                <AttributeNameField
                  data={attribute}
                  setData={setAttribute}
                  hintTextEn="Waterproof"
                  hintTextAr="مقاوم للماء"
                />
              </div>
            </div>
          )}

          {selectedOption === AttributeType.SELECT && (
            <div className="select">
              <div className="d-flex content">
                <img src={QuestionMark} alt="..." />
                <p>
                  Text attributes can be any attribute with multiple options,
                  for example: Color, Brand, Material...
                </p>
              </div>{' '}
              <div className="color">
                <AttributeNameField
                  data={attribute}
                  setData={setAttribute}
                  hintTextEn="Color"
                  hintTextAr="اللّون"
                />
              </div>
              {renderSelectValues()}
            </div>
          )}

          {selectedOption === AttributeType.NUMERIC && (
            <div className="select">
              <div className="d-flex content">
                <img src={QuestionMark} alt="..." />
                <p>
                  Numeric attributes are attributes with a number as their
                  value.
                </p>
              </div>{' '}
              <div className="color">
                <AttributeNameField
                  data={attribute}
                  setData={setAttribute}
                  hintTextEn="Capacity"
                  hintTextAr="السّعة"
                />
              </div>
              <div className="unit">
                <label>Unit</label>
                <input
                  type="text"
                  name="unit"
                  value={attribute.unit}
                  onChange={(e) => {
                    const name = e.target.name;
                    const value = e.target.value;
                    if (name === 'unit') {
                      if (value.length > 5) {
                        return;
                      }
                    }
                    setAttribute({ ...attribute, [name]: value });
                  }}
                  className="form-control"
                  placeholder="Gb"
                />
              </div>
              {renderNumericValues()}
            </div>
          )}

          <div className="butn">
            <Button className="butn" onClick={submit} color="success">
              Add Attribute
            </Button>
          </div>
        </div>
      </Wrapper>
    </Modal>
  );
};

const Wrapper = styled.div`
  ::placeholder {
    color: #e8e8e8;
  }
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  font-family: 'Lato';
  font-style: normal;
  .head_title {
    margin-top: -12px;
  }
  .containerr {
    /* border: 1px solid red; */

    p {
      padding: 25px 0px;
      line-height: 21px;
    }
  }
  option {
    border: 1px solid #e8e8e8;
  }
  .dropdown {
  }
  .options {
    border: 1px solid #e8e8e8;
    border-radius: 5px;
    width: 100% !important;
    padding: 13px;
    font-family: 'Lato';
    font-style: normal;
    font-weight: 400;
    font-size: 17px;
  }
  label {
    color: #00273380;
    margin: 0px 0px 20px 0px !important;
    width: 100%;
    border: none;
  }

  .butn {
    float: right;
    margin-top: 20px;
  }
  .content {
    margin-top: 22px;
    img {
      margin-bottom: 18px;
      margin-right: 10px;
    }
  }
  .butn2 {
    border: 1px solid #05ae4b;
    border-radius: 5px;
  }
  input {
    margin-bottom: 18px;

    border: 1px solid #e8e8e8;
    border-radius: 5px;
    width: 100% !important;
    padding: 5px 13px;
    font-family: 'Lato';
    font-style: normal;
    font-weight: 400;
    font-size: 17px;
  }
  .Bool {
    .contents {
      display: flex;
      img {
        margin: 0px 10px;
      }
    }
  }
`;

export default CustomAttributeModal;
